import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  X, Search, PlayCircle, BookOpen, AlertTriangle, Lightbulb, HelpCircle, ArrowRight,
  ArrowLeft, ListChecks, Table2, Sparkles, Crosshair, RotateCcw, Maximize2, Check, Printer,
} from 'lucide-react';
import api from '../../services/api';
import { useHelp } from './HelpContext';
import { safeInnerHtml } from '../../utils/sanitizeHtml';
import { buildHelpBook, printHelp } from '../../utils/helpBook';
import './HelpSidebar.css';

// Route path → help slug, WITHOUT a list to maintain.
//
// A page's slug is its route with the slashes written as hyphens
// (/rcm/denials → rcm-denials), so the slug for the current route can simply
// be looked up among the slugs that exist. The hand-kept map this replaced
// covered 14 routes out of 228; every page documented after it was written was
// unreachable from the page it documents.
const looksLikeAnId = (seg) => /^\d+$/.test(seg) || /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(seg) ||
                               /^[A-Z]{2,4}\d{4,}/.test(seg);

const resolveSlug = (pathname, slugs) => {
  if (!slugs || slugs.size === 0) return null;
  const parts = pathname.split('/').filter(Boolean);
  // A record's own page: /patients/9f2c… is documented as patient-detail.
  if (parts.length > 1 && looksLikeAnId(parts[parts.length - 1])) {
    const parent = parts[parts.length - 2];
    for (const cand of [`${parent}-detail`, `${parent.replace(/s$/, '')}-detail`]) {
      if (slugs.has(cand)) return cand;
    }
  }
  // Otherwise the longest prefix that has a document wins, so a route with no
  // document of its own still lands on its section's.
  for (let n = parts.length; n > 0; n--) {
    const cand = parts.slice(0, n).join('-');
    if (slugs.has(cand)) return cand;
  }
  return null;
};

// ── Panel width ────────────────────────────────────────────────────────────
// 420px was too narrow to read a walkthrough in, which is why the panel felt
// cramped while most of the screen sat empty behind a scrim. The reader sets
// the width now and we remember it.
const WIDTH_KEY = 'helpPanelWidth';
const MIN_W = 380;
const MAX_W = 1180;
const readWidth = () => {
  const n = parseInt(localStorage.getItem(WIDTH_KEY) || '', 10);
  if (Number.isFinite(n)) return Math.min(MAX_W, Math.max(MIN_W, n));
  return Math.min(880, Math.round(window.innerWidth * 0.62));
};

// ── Step progress ──────────────────────────────────────────────────────────
// Following a procedure on one screen while doing it on another is where
// people lose their place. Ticking a step keeps it, per page, across sessions.
const DONE_KEY = 'helpStepsDone';
const readDone = () => { try { return JSON.parse(localStorage.getItem(DONE_KEY) || '{}'); } catch { return {}; } };
const writeDone = (v) => { try { localStorage.setItem(DONE_KEY, JSON.stringify(v)); } catch {} };

// Point at the thing being described, on the real page behind the panel.
const showMe = (selector) => {
  if (!selector) return;
  let el = null;
  try { el = document.querySelector(selector); } catch { return; }
  if (!el) return;
  document.body.classList.add('help-peeking');       // fade the scrim out of the way
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('help-spotlight');
  setTimeout(() => {
    el.classList.remove('help-spotlight');
    document.body.classList.remove('help-peeking');
  }, 2600);
};

const Section = ({ id, icon: Icon, title, count, children, innerRef }) => (
  <section className="help-section" id={`help-sec-${id}`} ref={innerRef}>
    <h3 className="help-section__title">
      <Icon size={16} />
      <span>{title}</span>
      {count != null && <span className="help-section__count">{count}</span>}
    </h3>
    <div className="help-section__body">{children}</div>
  </section>
);

export default function HelpSidebar() {
  const { sidebarOpen, closeSidebar, activeSlug, setActiveSlug, startTour, maybeAutoLaunchFirstVisitTour } = useHelp();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hits, setHits] = useState([]);
  const [slugs, setSlugs] = useState(() => new Set());
  const [width, setWidth] = useState(readWidth);
  const [dragging, setDragging] = useState(false);
  const [done, setDone] = useState(readDone);
  const [activeSection, setActiveSection] = useState('overview');
  const [cameFrom, setCameFrom] = useState(null);   // breadcrumb after a search jump
  const bodyRef = useRef(null);
  const searchRef = useRef(null);
  const sectionRefs = useRef({});

  // The index of what is documented — this is what makes route→slug automatic.
  useEffect(() => {
    let cancelled = false;
    api.get('/help/pages')
      .then(r => { if (!cancelled) setSlugs(new Set((r.data || []).map(p => p.slug))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const routeSlug = useMemo(() => resolveSlug(location.pathname, slugs), [location.pathname, slugs]);

  // Keep activeSlug in sync with the current route when the sidebar is closed.
  useEffect(() => { if (routeSlug) { setActiveSlug(routeSlug); setCameFrom(null); } }, [routeSlug, setActiveSlug]);

  // First-visit auto-launch
  useEffect(() => { if (routeSlug) maybeAutoLaunchFirstVisitTour(routeSlug); }, [routeSlug, maybeAutoLaunchFirstVisitTour]);

  // Load page content on demand
  useEffect(() => {
    if (!sidebarOpen || !activeSlug) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/help/pages/${activeSlug}`)
      .then(r => { if (!cancelled) { setPage(r.data); setActiveSection('overview'); } })
      .catch(() => { if (!cancelled) setPage(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sidebarOpen, activeSlug]);

  // Debounced search
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      api.get('/help/search', { params: { q: searchTerm, limit: 12 } })
        .then(r => setHits(r.data || []))
        .catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Esc closes, "/" jumps to the search box.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { closeSidebar(); return; }
      if (e.key === '/' && document.activeElement !== searchRef.current) {
        e.preventDefault(); searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen, closeSidebar]);

  // Drag the left edge to resize.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const w = Math.min(MAX_W, Math.max(MIN_W, window.innerWidth - e.clientX));
      setWidth(w);
    };
    const onUp = () => { setDragging(false); localStorage.setItem(WIDTH_KEY, String(width)); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.classList.add('help-resizing');
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.classList.remove('help-resizing');
    };
  }, [dragging, width]);

  const sections = useMemo(() => {
    if (!page) return [];
    const s = [{ id: 'overview', title: 'Overview', icon: BookOpen }];
    if (page.flows?.length) s.push({ id: 'howto', title: 'How to', icon: ListChecks, count: page.flows.length });
    if (page.functions?.length) s.push({ id: 'controls', title: 'What everything does', icon: Sparkles, count: page.functions.length });
    if (page.fields?.length) s.push({ id: 'fields', title: 'Field reference', icon: Table2, count: page.fields.length });
    if (page.insider?.length) s.push({ id: 'insider', title: 'Worth knowing', icon: Lightbulb, count: page.insider.length });
    if (page.troubleshoot?.length) s.push({ id: 'trouble', title: 'Troubleshooting', icon: AlertTriangle, count: page.troubleshoot.length });
    return s;
  }, [page]);

  // Highlight the rail entry for whatever is on screen.
  useEffect(() => {
    if (!page || !bodyRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter(e => e.isIntersecting)
                         .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActiveSection(vis[0].target.id.replace('help-sec-', ''));
    }, { root: bodyRef.current, rootMargin: '-8px 0px -70% 0px', threshold: 0 });
    Object.values(sectionRefs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, [page, sections]);

  const jump = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stepKey = useCallback((fi, si) => `${page?.slug}:${fi}:${si}`, [page]);
  const toggleStep = (fi, si) => {
    const k = stepKey(fi, si);
    const next = { ...done, [k]: !done[k] };
    if (!next[k]) delete next[k];
    setDone(next); writeDone(next);
  };
  const resetFlow = (fi, steps) => {
    const next = { ...done };
    steps.forEach((_, si) => delete next[stepKey(fi, si)]);
    setDone(next); writeDone(next);
  };

  const openHit = (slug) => {
    if (page && slug !== page.slug) setCameFrom({ slug: page.slug, name: page.overview?.page_name });
    setActiveSlug(slug); setSearchTerm(''); setHits([]);
  };

  if (!sidebarOpen) return null;

  const searching = searchTerm.trim().length >= 2;

  return (
    <>
      <div className="help-sidebar__scrim" onClick={closeSidebar} />
      <aside className="help-panel" role="dialog" aria-label="Help" style={{ width }}>
        <div className="help-panel__grip" onMouseDown={() => setDragging(true)}
             title="Drag to resize" role="separator" aria-orientation="vertical" />

        <header className="help-panel__header">
          <div className="help-panel__id">
            <h2>Help</h2>
            {page && (
              <div className="help-panel__subtitle">
                {page.overview?.page_name}
                <span className="help-chip">{page.category}</span>
                {page.overview?.location && <span className="help-panel__loc">{page.overview.location}</span>}
              </div>
            )}
          </div>
          <div className="help-panel__actions">
            {page && (
              <button className="help-icon-btn"
                      onClick={() => printHelp(buildHelpBook(page, { scope: 'page' }))}
                      title="Print these instructions — choose Save as PDF in the dialog">
                <Printer size={17} />
              </button>
            )}
            {page && (
              <Link to={`/help?page=${encodeURIComponent(page.slug)}`} onClick={closeSidebar}
                    className="help-icon-btn" title="Open the full guide">
                <Maximize2 size={17} />
              </Link>
            )}
            <button className="help-icon-btn" onClick={closeSidebar} aria-label="Close help"><X size={19} /></button>
          </div>
        </header>

        <div className="help-panel__search">
          <Search size={16} />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search every page's instructions…   (press / )"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            autoFocus
          />
          {searchTerm && (
            <button className="help-panel__search-clear" onClick={() => setSearchTerm('')} aria-label="Clear"><X size={15} /></button>
          )}
        </div>

        {searching ? (
          <div className="help-panel__results">
            {hits.length === 0
              ? <div className="help-empty"><Search size={34} strokeWidth={1.2} /><p>Nothing matched “{searchTerm}”.</p></div>
              : hits.map(h => (
                <button key={h.slug} className="help-hit" onClick={() => openHit(h.slug)}>
                  <div className="help-hit__title">
                    {h.page_name}
                    <span className="help-chip">{h.category}</span>
                    <span className="help-hit__where">in {h.matched_section}</span>
                  </div>
                  <div className="help-hit__snippet" dangerouslySetInnerHTML={safeInnerHtml(h.snippet)} />
                </button>
              ))
            }
          </div>
        ) : (
          <div className="help-panel__split">
            {/* The rail — this is what the empty column was for. */}
            {page && sections.length > 1 && (
              <nav className="help-rail" aria-label="Sections">
                {sections.map(s => (
                  <button key={s.id}
                          className={`help-rail__item ${activeSection === s.id ? 'is-active' : ''}`}
                          onClick={() => jump(s.id)}>
                    <s.icon size={15} />
                    <span>{s.title}</span>
                    {s.count != null && <em>{s.count}</em>}
                  </button>
                ))}
                {(page.tours || []).length > 0 && (
                  <div className="help-rail__tours">
                    <div className="help-rail__label">Guided tours</div>
                    {page.tours.map(t => (
                      <button key={t.name} className="help-rail__tour" onClick={() => { closeSidebar(); startTour(page.slug, t.name); }}>
                        <PlayCircle size={14} /> {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </nav>
            )}

            <div className="help-panel__body" ref={bodyRef}>
              {cameFrom && (
                <button className="help-backlink" onClick={() => { setCameFrom(null); setActiveSlug(cameFrom.slug); }}>
                  <ArrowLeft size={14} /> Back to {cameFrom.name}
                </button>
              )}

              {loading && <div className="help-empty"><p>Loading…</p></div>}

              {!loading && !page && (
                <div className="help-empty">
                  <HelpCircle size={40} strokeWidth={1.2} />
                  <h3>No instructions for this page yet</h3>
                  <p>Search above for a topic, or browse every page in the Help Center.</p>
                  <Link to="/help" onClick={closeSidebar} className="help-cta">Open Help Center <ArrowRight size={14} /></Link>
                </div>
              )}

              {!loading && page && (
                <>
                  <Section id="overview" icon={BookOpen} title="Overview"
                           innerRef={el => (sectionRefs.current.overview = el)}>
                    <p className="help-lede">{page.overview.purpose}</p>
                    {page.overview.benefit && (
                      <div className="help-benefit">
                        <Lightbulb size={15} />
                        <span><strong>Why it matters:</strong> {page.overview.benefit}</span>
                      </div>
                    )}
                    {page.overview.when_to_use && (
                      <p className="help-muted"><strong>When to use it:</strong> {page.overview.when_to_use}</p>
                    )}
                  </Section>

                  {!!page.flows?.length && (
                    <Section id="howto" icon={ListChecks} title="How to" count={page.flows.length}
                             innerRef={el => (sectionRefs.current.howto = el)}>
                      {page.flows.map((f, fi) => {
                        const total = f.steps.length;
                        const complete = f.steps.filter((_, si) => done[stepKey(fi, si)]).length;
                        const pct = total ? Math.round((complete / total) * 100) : 0;
                        return (
                          <div key={f.name} className="help-flow">
                            <div className="help-flow__head">
                              <h4>{f.name}</h4>
                              <div className="help-flow__meta">
                                <span>{complete}/{total}</span>
                                {complete > 0 && (
                                  <button className="help-mini-btn" onClick={() => resetFlow(fi, f.steps)} title="Clear the ticks">
                                    <RotateCcw size={13} />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="help-progress"><span style={{ width: `${pct}%` }} /></div>
                            <ol className="help-steps">
                              {f.steps.map((s, si) => {
                                const isDone = !!done[stepKey(fi, si)];
                                return (
                                  <li key={si} className={isDone ? 'is-done' : ''}>
                                    <button className="help-step__tick" onClick={() => toggleStep(fi, si)}
                                            aria-pressed={isDone} aria-label={isDone ? 'Mark not done' : 'Mark done'}>
                                      {isDone ? <Check size={13} /> : <span className="help-step__num">{si + 1}</span>}
                                    </button>
                                    <div className="help-step__text">
                                      <div className="help-step__title">
                                        {s.title}
                                        {s.variation && s.variation !== 'basic' && (
                                          <span className="help-tag">{s.variation}</span>
                                        )}
                                      </div>
                                      <div className="help-step__desc">{s.description}</div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        );
                      })}
                    </Section>
                  )}

                  {!!page.functions?.length && (
                    <Section id="controls" icon={Sparkles} title="What everything does" count={page.functions.length}
                             innerRef={el => (sectionRefs.current.controls = el)}>
                      <div className="help-cards">
                        {page.functions.map(f => (
                          <div key={f.name} className="help-card">
                            <div className="help-card__head">
                              <span className="help-card__name">{f.name}</span>
                              {f.kind && <span className="help-tag">{f.kind}</span>}
                              {f.selector && (
                                <button className="help-mini-btn help-mini-btn--show" onClick={() => showMe(f.selector)}
                                        title="Point it out on the page">
                                  <Crosshair size={13} /> Show me
                                </button>
                              )}
                            </div>
                            <div className="help-card__desc">{f.description}</div>
                            {f.benefit && (
                              <div className="help-benefit help-benefit--sm">
                                <Lightbulb size={13} /><span>{f.benefit}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {!!page.fields?.length && (
                    <Section id="fields" icon={Table2} title="Field reference" count={page.fields.length}
                             innerRef={el => (sectionRefs.current.fields = el)}>
                      <div className="help-table-wrap">
                        <table className="help-table">
                          <thead><tr><th>Field</th><th>What it means</th><th>Accepted values</th></tr></thead>
                          <tbody>
                            {page.fields.map(f => (
                              <tr key={f.name}>
                                <td>
                                  <strong>{f.name}</strong>
                                  {f.required && <span className="help-required">required</span>}
                                </td>
                                <td>
                                  {f.meaning}
                                  {f.examples?.length ? <div className="help-muted">e.g. {f.examples.join(', ')}</div> : null}
                                </td>
                                <td>{f.valid_values || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Section>
                  )}

                  {!!page.insider?.length && (
                    <Section id="insider" icon={Lightbulb} title="Worth knowing" count={page.insider.length}
                             innerRef={el => (sectionRefs.current.insider = el)}>
                      <div className="help-cards">
                        {page.insider.map((i, idx) => (
                          <div key={idx} className={`help-note help-note--${i.kind || 'default'}`}>
                            <div className="help-note__topic">{i.topic}{i.kind && <span className="help-tag">{i.kind.replace('_', ' ')}</span>}</div>
                            <div className="help-note__detail">{i.detail}</div>
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {!!page.troubleshoot?.length && (
                    <Section id="trouble" icon={AlertTriangle} title="Troubleshooting" count={page.troubleshoot.length}
                             innerRef={el => (sectionRefs.current.trouble = el)}>
                      {page.troubleshoot.map((t, idx) => (
                        <details key={idx} className="help-ts">
                          <summary>{t.symptom}</summary>
                          <p className="help-muted"><strong>Why it happens:</strong> {t.cause}</p>
                          <ol>{t.fix.map((f, i) => <li key={i}>{f}</li>)}</ol>
                        </details>
                      ))}
                    </Section>
                  )}

                  <div className="help-panel__footer">
                    <Link to={`/help?page=${encodeURIComponent(page.slug)}`} onClick={closeSidebar} className="help-cta">
                      Open the full guide — printable <ArrowRight size={14} />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
